import { Link, Navigate } from 'react-router-dom'
import {
  Car, CheckCircle2, XCircle, Camera, FileText,
  Wrench, ArrowRight, AlertTriangle, Star,
  BadgeCheck, ChevronRight, TrendingDown,
  Check, CircleSlash,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Seo } from '@/components/Seo'
import { useAuth } from '@/contexts/AuthContext'

const DEPOIMENTOS = [
  {
    nome: "Rafael Mendes",
    cidade: "São Paulo, SP",
    texto: "Descobri R$ 2.400 de problema antes de fechar. Descontei na oferta e ainda saí com margem. Sem o WeAuto eu teria decidido no escuro.",
  },
  {
    nome: "Marcos Oliveira",
    cidade: "Belo Horizonte, MG",
    texto: "Eu controlava tudo de cabeça. Agora avalio, registro foto, custo e fechamento no mesmo lugar. A negociação ficou muito mais segura.",
  },
]

const COMO_FUNCIONA = [
  {
    numero: "1",
    titulo: "Cadastre o veículo",
    descricao: "Placa, marca, modelo e foto. Em 30 segundos o carro está pronto para avaliação.",
    Icone: Car,
  },
  {
    numero: "2",
    titulo: "Avalie item por item",
    descricao: "Fotografe cada ponto do veículo. Motor, lataria, interior, rodas. Tudo documentado com status e custo.",
    Icone: Camera,
  },
  {
    numero: "3",
    titulo: "Feche com documento",
    descricao: "Soma automática dos defeitos encontrados. Gere um fechamento profissional para compra, consignado ou controle interno.",
    Icone: FileText,
  },
]

const BENEFICIOS = [
  {
    titulo: "Cada defeito tem foto",
    descricao: "Documentado na hora, com data. Registro claro do estado do carro na entrada, antes de qualquer acordo.",
    Icone: Camera,
  },
  {
    titulo: "Custo calculado na hora",
    descricao: "Você vê o impacto financeiro antes de fechar o negócio, ajustar a oferta ou aprovar reparos.",
    Icone: TrendingDown,
  },
  {
    titulo: "Fechamento em PDF",
    descricao: "Documento profissional com reparos, valores, observações e ciência por assinatura quando necessário.",
    Icone: FileText,
  },
]

const REPAROS = [
  { item: "Funilaria escondida", custo: "R$ 800 – 2.500", Icone: Wrench },
  { item: "Motor com vazamento", custo: "R$ 2.000 – 5.000", Icone: AlertTriangle },
  { item: "Câmbio com folga", custo: "R$ 1.500 – 4.000", Icone: AlertTriangle },
  { item: "Lataria repintada", custo: "R$ 1.200 – 3.000", Icone: TrendingDown },
]

const PLANOS = [
  {
    nome: "Básico",
    preco: "R$ 149",
    descricao: "Ideal para autônomos",
    recursos: ["20 avaliações por mês", "Gestão de veículos", "Fechamentos em PDF", "Consulta de placa", "Histórico FIPE", "Suporte por WhatsApp"],
    destaque: false,
  },
  {
    nome: "Pro",
    preco: "R$ 299",
    descricao: "Ideal para lojas e revendas",
    recursos: ["60 avaliações por mês", "Múltiplos avaliadores", "Relatórios avançados", "Avaliação 360° completa", "Prioridade no suporte", "Suporte por WhatsApp"],
    destaque: true,
  },
]

const DOR_ITEMS = [
  "Recebeu ou comprou um carro e só descobriu o problema depois.",
  "Consertou do próprio bolso o que não estava documentado.",
  "Tentou vender e o custo real do carro não fechou.",
  "Ficou com o carro parado porque a margem sumiu.",
  "Na entrada parecia bom. No fechamento virou discussão.",
  "Dependeu da memória para lembrar reparos, valores e combinados.",
]

export default function Landing() {
  const { user, loading } = useAuth()

  if (!loading && user) return <Navigate to="/dashboard" replace />

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Seo
        title="WeAuto | Avaliação veicular, fotos e fechamento para lojas"
        description="Documente entrada, avaliação e fechamento de veículos com fotos, custos e relatórios para lojas e revendas."
        image="/carro_defeito.webp"
        path="/"
      />

      {/* ── Navbar ─────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
              <Car className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="text-lg font-bold tracking-tight">WeAuto</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/login">Entrar</Link>
            </Button>
            <Button size="sm" asChild>
              <Link to="/cadastro">Começar grátis</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* ── Bloco 1: Hero ──────────────────────────────────── */}
      <section className="relative overflow-hidden bg-primary px-4 py-20 text-white sm:py-28">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-blue-800" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            {/* Copy */}
            <div className="space-y-6">
              <Badge className="border-white/30 bg-white/15 text-white hover:bg-white/20">
                Entrada, avaliação e fechamento veicular
              </Badge>
              <h1 className="text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
                Controle a entrada, avaliação e fechamento dos veículos da sua loja.
              </h1>
              <p className="max-w-lg text-lg leading-relaxed text-blue-100">
                Registre fotos, reparos, custos e documentos para comprar melhor,
                receber consignados com segurança e controlar carros de leilão sem depender da memória.
              </p>
              <Button
                size="lg"
                className="bg-white px-8 font-semibold text-primary hover:bg-blue-50"
                asChild
              >
                <Link to="/cadastro">
                  Começar minha primeira avaliação
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-blue-200">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Sem cartão de crédito
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" /> 5 avaliações grátis
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Suporte por WhatsApp
                </span>
              </div>
            </div>

            {/* Mock app */}
            <div className="mx-auto w-full max-w-sm">
              <div className="rounded-2xl border border-white/20 bg-white/10 p-1.5 shadow-2xl backdrop-blur">
                <div className="overflow-hidden rounded-xl bg-white text-foreground">
                  <div className="flex items-center gap-2 border-b bg-muted/40 px-4 py-3">
                    <div className="h-2 w-2 rounded-full bg-rose-500" />
                    <span className="text-sm font-semibold">Avaliação · Frontal</span>
                    <Badge variant="destructive" className="ml-auto text-xs">Reprovado</Badge>
                  </div>
                  <div className="px-4 pt-4">
                    <img
                      src="/carro_defeito.webp"
                      alt="Defeito identificado na avaliação"
                      className="aspect-video w-full rounded-lg object-cover"
                    />
                  </div>
                  <div className="space-y-2.5 p-4">
                    <div className="flex items-center justify-between rounded-lg border border-rose-100 bg-rose-50 px-3 py-2.5">
                      <div>
                        <p className="text-sm font-semibold text-rose-700">Para-choque dianteiro</p>
                        <p className="mt-0.5 text-xs text-rose-400">Amassado + quebrado</p>
                      </div>
                      <span className="font-bold text-rose-700">R$ 1.700</span>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-rose-100 bg-rose-50 px-3 py-2.5">
                      <div>
                        <p className="text-sm font-semibold text-rose-700">Para-lama dianteiro direito</p>
                        <p className="mt-0.5 text-xs text-rose-400">Riscos + possível amassado leve</p>
                      </div>
                      <span className="font-bold text-rose-700">R$ 800</span>
                    </div>
                    <div className="flex items-center justify-between border-t pt-2.5">
                      <span className="text-xs text-muted-foreground">Custo identificado</span>
                      <span className="font-bold text-rose-700">R$ 2.500,00</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Bloco 2: Reconhecimento da dor ─────────────────── */}
      <section className="bg-slate-950 px-4 py-16 text-white sm:py-20">
        <div className="mx-auto max-w-3xl space-y-8 text-center">
          <h2 className="text-2xl font-bold sm:text-3xl">Isso já aconteceu com você?</h2>
          <div className="grid gap-3 text-left sm:grid-cols-2">
            {DOR_ITEMS.map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3"
              >
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" />
                <span className="text-sm text-slate-300">{item}</span>
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 px-6 py-5 text-left">
            <p className="text-lg font-semibold">Não é azar.</p>
            <p className="mt-1 text-slate-300">
              É o que acontece quando o carro entra na operação sem avaliação documentada.
              O defeito, o reparo e o combinado até existiam. Só não estavam registrados de forma profissional.
            </p>
          </div>
        </div>
      </section>

      {/* ── Bloco 3: Amplificação com números ──────────────── */}
      <section className="bg-background px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-4xl space-y-10">
          <div className="space-y-3 text-center">
            <h2 className="text-2xl font-bold sm:text-3xl">Quanto você está deixando de ganhar?</h2>
            <p className="mx-auto max-w-xl text-muted-foreground">
              Um defeito não documentado na entrada vira custo, discussão ou margem perdida no fechamento.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {REPAROS.map(({ item, custo, Icone }, i) => (
              <div key={i} className="rounded-xl border bg-card p-5 shadow-sm space-y-3">
                <Icone className="h-5 w-5 text-rose-500" />
                <p className="font-semibold text-sm">{item}</p>
                <p className="font-bold text-rose-600">{custo}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-muted-foreground">
            Multiplique pelo número de carros que entram na sua loja por mês.{' '}
            <span className="font-semibold text-foreground">
              Esse é o tamanho do risco que você está deixando sem controle.
            </span>
          </p>
        </div>
      </section>

      {/* ── Bloco 4: Solução ────────────────────────────────── */}
      <section className="bg-muted/40 px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-4xl space-y-10 text-center">
          <div className="space-y-4">
            <Badge variant="secondary" className="font-medium text-primary">A solução</Badge>
            <h2 className="text-2xl font-bold sm:text-3xl">
              O WeAuto não é um app de avaliação.
            </h2>
            <p className="mx-auto max-w-xl text-lg leading-relaxed text-muted-foreground">
              É o fluxo que documenta o veículo da entrada ao fechamento,
              para você decidir com números, fotos e histórico.
            </p>
          </div>
          <div className="grid gap-4 text-left sm:grid-cols-3">
            {BENEFICIOS.map(({ titulo, descricao, Icone }, i) => (
              <div key={i} className="rounded-xl border bg-card p-5 shadow-sm space-y-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Icone className="h-5 w-5 text-primary" />
                </div>
                <p className="font-semibold">{titulo}</p>
                <p className="text-sm leading-relaxed text-muted-foreground">{descricao}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bloco 5: Como funciona ──────────────────────────── */}
      <section className="bg-background px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-4xl space-y-10">
          <div className="space-y-3 text-center">
            <h2 className="text-2xl font-bold sm:text-3xl">Como funciona</h2>
            <p className="text-muted-foreground">Três passos. Do celular para a negociação.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-3">
            {COMO_FUNCIONA.map(({ numero, titulo, descricao, Icone }, i) => (
              <div key={i} className="relative text-center">
                {i < COMO_FUNCIONA.length - 1 && (
                  <ChevronRight className="absolute -right-4 top-5 hidden h-6 w-6 text-border sm:block" />
                )}
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
                  <Icone className="h-7 w-7 text-primary" />
                </div>
                <div className="mt-4 space-y-1.5">
                  <div className="flex items-center justify-center gap-2">
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">
                      {numero}
                    </span>
                    <p className="font-semibold">{titulo}</p>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">{descricao}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Mock da tela de avaliação */}
          <div className="mt-12 flex flex-col items-center gap-4">
            <p className="text-sm font-medium text-muted-foreground">É assim na prática</p>
            <div className="w-full max-w-sm overflow-hidden rounded-2xl border border-border/70 bg-white shadow-lg">
              {/* Header do mock */}
              <div className="flex items-center justify-between border-b bg-muted/40 px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-rose-500" />
                  <span className="text-sm font-semibold">Avaliação 360° · Frontal</span>
                </div>
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Salvo</span>
              </div>
              {/* Critério */}
              <div className="space-y-1 px-4 pt-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-sm">Para-choque dianteiro</p>
                    <p className="text-xs text-muted-foreground">Amassado + quebrado</p>
                  </div>
                  <Badge variant="destructive" className="text-xs">Reprovado</Badge>
                </div>
              </div>
              {/* Foto + custo estimado */}
              <div className="mx-4 mb-3 flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Camera className="h-3.5 w-3.5" />
                  <span className="text-xs">1 foto anexada</span>
                </div>
                <span className="text-xs font-semibold text-rose-700">R$ 800 — reparo estimado</span>
              </div>
              {/* Botões */}
              <div className="grid grid-cols-4 gap-2 p-4">
                <div className="flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl border border-border/70 bg-white px-2 py-2.5 text-center">
                  <Check className="h-4 w-4 text-emerald-600" />
                  <span className="text-xs font-medium text-muted-foreground">Aprovado</span>
                </div>
                <div className="flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl border border-border/70 bg-white px-2 py-2.5 text-center">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <span className="text-xs font-medium text-muted-foreground">Atenção</span>
                </div>
                <div className="flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl border-2 border-rose-600 bg-rose-50 px-2 py-2.5 text-center">
                  <XCircle className="h-4 w-4 text-rose-700" />
                  <span className="text-xs font-bold text-rose-700">Reprovado</span>
                </div>
                <div className="flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl border border-border/70 bg-white px-2 py-2.5 text-center">
                  <CircleSlash className="h-4 w-4 text-slate-500" />
                  <span className="text-xs font-medium text-muted-foreground">Isento</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Bloco 6: Objeção ────────────────────────────────── */}
      <section className="bg-muted/40 px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-2xl">
          <div className="rounded-2xl border bg-card p-8 shadow-sm space-y-6">
            <div className="space-y-2">
              <p className="text-lg font-bold">"Eu já sei avaliar carro."</p>
              <p className="text-muted-foreground">
                Com certeza. E é exatamente por isso que o WeAuto foi feito para você.
              </p>
            </div>
            <div className="space-y-3">
              {[
                "Você enxerga o problema. O WeAuto documenta com foto, data e custo estimado.",
                "Você sabe negociar. O WeAuto te dá os números para negociar com base em dados.",
                "Você conhece o carro. O WeAuto registra tudo para que ninguém conteste depois.",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <p className="text-sm text-muted-foreground">{item}</p>
                </div>
              ))}
            </div>
            <p className="border-t pt-4 text-sm font-semibold">
              O WeAuto não substitui seu olho. Ele protege o seu bolso.
            </p>
          </div>
        </div>
      </section>

      {/* ── Bloco 7: Depoimentos ────────────────────────────── */}
      <section className="bg-background px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-4xl space-y-10">
          <h2 className="text-center text-2xl font-bold sm:text-3xl">
            Quem usa não decide mais no escuro
          </h2>
          <div className="grid gap-6 sm:grid-cols-2">
            {DEPOIMENTOS.map((d, i) => (
              <div key={i} className="rounded-2xl border bg-card p-6 shadow-sm space-y-4">
                <div className="flex gap-1">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">"{d.texto}"</p>
                <div className="border-t pt-4">
                  <p className="text-sm font-semibold">{d.nome}</p>
                  <p className="text-xs text-muted-foreground">{d.cidade}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bloco 8: Planos ─────────────────────────────────── */}
      <section className="bg-muted/40 px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-4xl space-y-10">
          <div className="space-y-3 text-center">
            <h2 className="text-2xl font-bold sm:text-3xl">Planos simples, sem surpresa</h2>
            <p className="text-muted-foreground">Comece com 5 avaliações gratuitas. Sem cartão de crédito.</p>
          </div>
          <div className="mx-auto grid max-w-2xl gap-6 sm:grid-cols-2">
            {PLANOS.map((plano, i) => (
              <div
                key={i}
                className={`rounded-2xl border p-6 space-y-5 ${
                  plano.destaque
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'bg-card shadow-sm'
                }`}
              >
                {plano.destaque && (
                  <Badge className="bg-primary text-xs text-primary-foreground">Mais popular</Badge>
                )}
                <div>
                  <p className="text-xl font-bold">{plano.nome}</p>
                  <p className="text-sm text-muted-foreground">{plano.descricao}</p>
                </div>
                <div>
                  <span className="text-3xl font-extrabold">{plano.preco}</span>
                  <span className="text-sm text-muted-foreground">/mês</span>
                </div>
                <ul className="space-y-2">
                  {plano.recursos.map((r, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                      {r}
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full"
                  variant={plano.destaque ? 'default' : 'outline'}
                  asChild
                >
                  <Link to="/cadastro">Começar grátis</Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bloco 9: CTA Final ──────────────────────────────── */}
      <section className="bg-primary px-4 py-20 text-white">
        <div className="mx-auto max-w-2xl space-y-6 text-center">
          <h2 className="text-3xl font-extrabold leading-tight sm:text-4xl">
            Quantos veículos ainda vão entrar sem controle?
          </h2>
          <p className="text-lg text-blue-100">
            Cada entrada sem documentação é um risco que você assume sozinho.
            Comece agora. É grátis.
          </p>
          <Button
            size="lg"
            className="bg-white px-10 font-semibold text-primary hover:bg-blue-50"
            asChild
          >
            <Link to="/cadastro">
              Começar minha primeira avaliação
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-blue-200">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" /> Sem cartão de crédito
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" /> 5 avaliações gratuitas
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" /> Cancele quando quiser
            </span>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────── */}
      <footer className="border-t bg-background px-4 py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 sm:flex-row sm:justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary">
              <Car className="h-3.5 w-3.5 text-primary-foreground" />
            </div>
            <span className="text-sm font-semibold">WeAuto</span>
            <span className="text-xs text-muted-foreground">· Entrada, avaliação e fechamento veicular</span>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <Link to="/termos-de-uso" className="transition-colors hover:text-foreground">
              Termos de Uso
            </Link>
            <Link to="/politica-de-privacidade" className="transition-colors hover:text-foreground">
              Privacidade
            </Link>
            <Link to="/login" className="transition-colors hover:text-foreground">
              Entrar
            </Link>
          </div>
        </div>
      </footer>

    </div>
  )
}
